import { notFound } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { eq, inArray } from "drizzle-orm"
import { z } from "zod"

import {
  hasPolicyChangePage,
  scoringTypeKeysOf,
  walkthroughMarks,
} from "@/components/terminal/policy-change/policy-change"
import { deliveredEntries } from "@/db/queries/terminal-file-reader"
import { terminalCompany } from "@/db/schema/terminal-company-identity"
import { terminalAssessment } from "@/db/schema/terminal-file"
import { db } from "@/lib/admin-db"
import { assertPortfolioAccess } from "@/lib/supabase/auth"
import { latestCompletedAssessment } from "@/terminal/file/live-parts"
import { readFileSummaries } from "@/terminal/file/read-file"
import {
  loadTerminalPolicy,
  newestPolicyVersions,
} from "@/terminal/policies/load-terminal-policy"

// Each monitored Company in the Portfolio segment with its Policy 1.0 grade
// (the legacy posture) and its Policy 2.0 grade (its File as the customer
// Terminal serves it), with the File's evidence reduced to the walkthrough's
// marks. A Company with no File yet has no Policy 2.0 grade. Read on every
// load, so the page follows the File by itself.
export const loadPolicyChange = createServerFn({ method: "GET" })
  .inputValidator(z.object({ portfolioSlug: z.string().min(1) }))
  .handler(async ({ data }) => {
    // A server function is directly callable, so the route guard is not enough.
    const { portfolioId } = await assertPortfolioAccess(data)
    // Only the Portfolios that get the page.
    if (!hasPolicyChangePage(data.portfolioSlug)) throw notFound()
    const [{ productionAssessmentByLegacyCompanyId }, entries] =
      await Promise.all([
        // Server-only: the megabyte of legacy Briefs stays off the client.
        import("@/admin/monitoring/production-assessments"),
        deliveredEntries(portfolioId),
      ])
    const members = entries.filter(({ isMonitoring }) => isMonitoring)
    const companyIds = members.map(({ companyId }) => companyId)
    // The marks read the Score Breakdown, which the File summary never
    // carries, so it is read here, server-side, for the marks alone, in the
    // summaries' snapshot so both read the same Assessment.
    const [companies, summaryByCompanyId] = await db.transaction(
      (tx) =>
        Promise.all([
          companyIds.length === 0
            ? []
            : tx
                .select({
                  id: terminalCompany.id,
                  legacyCompanyId: terminalCompany.legacy_company_id,
                  scoreBreakdown: terminalAssessment.score_breakdown,
                })
                .from(terminalCompany)
                .leftJoin(
                  terminalAssessment,
                  eq(
                    terminalAssessment.id,
                    latestCompletedAssessment(tx, {
                      companyId: terminalCompany.id,
                    })
                  )
                )
                .where(inArray(terminalCompany.id, companyIds)),
          readFileSummaries(
            companyIds.map((companyId) => ({ companyId })),
            { fields: ["detections"], reader: tx }
          ),
        ]),
      { isolationLevel: "repeatable read", accessMode: "read only" }
    )
    const companyById = new Map(
      companies.map((company) => [company.id, company])
    )
    const scoringTypeKeys = scoringTypeKeysOf(
      loadTerminalPolicy(newestPolicyVersions)
    )
    return {
      portfolioSlug: data.portfolioSlug,
      companies: members.map((member) => {
        const company = companyById.get(member.companyId)
        const legacyId = company?.legacyCompanyId
        const summary = summaryByCompanyId.get(member.companyId)
        return {
          id: member.companyId,
          name: member.companyName,
          slug: member.companySlug,
          before:
            (legacyId &&
              productionAssessmentByLegacyCompanyId[legacyId]?.posture) ||
            null,
          after: summary
            ? {
                band: summary.assessment.band,
                score: summary.assessment.score,
                marks: walkthroughMarks({
                  band: summary.assessment.band,
                  breakdown: company!.scoreBreakdown!,
                  activeTypeKeys: new Set(
                    summary.detections.map(({ typeKey }) => typeKey)
                  ),
                  scoringTypeKeys,
                }),
              }
            : null,
        }
      }),
    }
  })

export type PolicyChangeData = Awaited<ReturnType<typeof loadPolicyChange>>
export type PolicyChangeCompany = PolicyChangeData["companies"][number]
